<script>
import Monitor from "@/services/Monitor";

export default {
  data() {
    return {
      tenant: "",
      db_name: "",
      srcDb: "",
      dstDb: "",
      hideOk: false,
      showCharsets: false,
      flagC: false,
      flagI: false,
      diffs: [],
      isDiffLoading: false,
      selectedDiff: null,
      showDiffModal: false,
      collapseState: false,
      diffRequests: [],
      isBusy: false,
      expandedRows: {},
    };
  },
  methods: {
    async getDiffs(srcDb, dstDb, hideOk) {
      this.isDiffLoading = true;
      try {
        const params = {
          src: srcDb || undefined,
          dst: dstDb || undefined,
          hide_ok: hideOk ? true : undefined,
        };
        const res = await Monitor.getDiffData(params.src, params.dst, params.hide_ok);
        this.diffs = res.data.data;
      } catch (e) {
        console.error(e);
        this.diffs = [];
      } finally {
        this.isDiffLoading = false;
      }
    },
    async requestDiffs() {
      this.isDiffLoading = true;
      try {
        let flags = "-a";
        if (this.flagC) flags += "c";
        if (this.flagI) flags += "i";

        const body = {
          "db_name": this.db_name,
          "tenant": this.tenant,
        };
        const res = await Monitor.callDiffScript(body, flags);
        console.log(res);
        this.diffs = res.data.data;
      } catch (e) {
        console.error(e);
        this.diffs = [];
      } finally {
        this.isDiffLoading = false;
      }
    },
    async getDiffRequests() {
      this.isBusy = true;
      try {
        const res = await Monitor.getDiffRequests();
        this.diffRequests = res.data || [];
      } catch (e) {
        console.error(e);
        this.diffRequests = [];
      } finally {
        this.isBusy = false;
      }
    },
    openDiffModal(diff) {
      this.selectedDiff = diff;
      this.showDiffModal = true;
    },
    closeDiffModal() {
      this.selectedDiff = null;
      this.showDiffModal = false;
    },
    toggleRowExpansion(tableName) {
      this.$set(this.expandedRows, tableName, !this.expandedRows[tableName]);
    },
    toggleCollapse() {
      this.collapseState = !this.collapseState;
    },
  },
  computed: {
    iconClass() {
      return this.collapseState ? "mdi mdi-chevron-up" : "mdi mdi-chevron-down";
    },
  },
  mounted() {
    this.getDiffRequests(); // load requests on mount
  },
};
</script>

<template>
  <div>
    <!-- Collapsible Form Header -->
    <div class="card p-3 mb-3">
      <a
        href="javascript:void(0);"
        class="text-body fw-semibold pb-2 d-block"
        @click="toggleCollapse"
        role="button"
        aria-expanded="false"
        v-b-toggle="'collapseDiff'"
      >
        <i :class="iconClass" class="me-1" />Database Diff Request Form
      </a>
      <b-collapse id="collapseDiff" class="mt-2">
        <!-- Source Connection -->
        <div class="d-flex">
          <div class="w-50">
            <h5>Tenant code</h5>
            <div class="row">
              <div class="col-md-3">
                <b-form-group label="Tenant">
                  <b-form-input v-model="tenant" placeholder="tenant 3 letter code" />
                </b-form-group>
              </div>
            </div>
          </div>
          <div class="w-50">
            <h5>Database name</h5>
            <div class="row">
              <div class="col-md-3">
                <b-form-group label="Database">
                  <b-form-input v-model="db_name" placeholder="database name" />
                </b-form-group>
              </div>
            </div>
          </div>
          <!-- Comparison Connection -->
        </div>

        <!-- Flags -->
        <div class="row mb-4">
          <div class="col-md-6 d-flex align-items-center">
            <div class="form-check form-switch form-switch-lg mb-3">
              <input
                v-model="flagC"
                type="checkbox"
                class="form-check-input"
                id="switchC"
                true-value="1"
                false-value="0"
              />
              <label class="form-check-label h6 ms-2" for="switchC">Ignore Charset (-c)</label>
            </div>
          </div>
          <div class="col-md-6 d-flex align-items-center">
            <div class="form-check form-switch form-switch-lg mb-3">
              <input
                v-model="flagI"
                type="checkbox"
                class="form-check-input"
                id="switchI"
                true-value="1"
                false-value="0"
              />
              <label class="form-check-label h6 ms-2" for="switchI">Reverse Direction (-i)</label>
            </div>
          </div>
        </div>

        <!-- Action Button -->
        <div class="row mb-3">
          <div class="col-md-12 d-flex justify-content-end">
            <b-button variant="primary" @click="requestDiffs">Run Diff</b-button>
          </div>
        </div>
      </b-collapse>
    </div>
    <!-- Diff Requests Table Card -->
    <div class="card p-3 mb-3">
      <h5 class="mb-3">Pending Diff Requests</h5>
      <div class="table-responsive">
        <b-table
          :items="diffRequests"
          :fields="[
            { key: 'id', label: 'ID' },
            { key: 'src_db', label: 'Source DB' },
            { key: 'dst_db', label: 'Destination DB' },
            { key: 'include_charset', label: 'Include Charset' },
            { key: 'invert', label: 'Invert' },
            { key: 'status', label: 'Status' },
            { key: 'created_at', label: 'Created At' }
          ]"
          responsive="sm"
          show-empty
          empty-text="No diff requests found"
        >
          <template v-slot:cell(include_charset)="row">
            <span>{{ row.item.include_charset ? 'Yes' : 'No' }}</span>
          </template>
          <template v-slot:cell(invert)="row">
            <span>{{ row.item.invert ? 'Yes' : 'No' }}</span>
          </template>
          <template v-slot:cell(status)="row">
            <span :class="{
              'text-success': row.item.status === 'completed',
              'text-warning': row.item.status === 'pending',
              'text-danger': row.item.status === 'failed'
            }">{{ row.item.status }}</span>
          </template>
        </b-table>
      </div>
    </div>

    <!-- Filters Row (Inputs, Checkboxes, Apply Button on one line) -->
    <div class="card p-3 mb-3">
      <div class="row align-items-center g-3">
        <div class="col-md-3">
          <b-form-group label="Source Database">
            <b-form-input v-model="srcDb" placeholder="source_db" />
          </b-form-group>
        </div>
        <div class="col-md-3">
          <b-form-group label="Destination Database">
            <b-form-input v-model="dstDb" placeholder="target_db" />
          </b-form-group>
        </div>
        <div class="col-md-2 d-flex align-items-center">
          <div class="form-check form-switch form-switch-lg mb-0">
            <input
              v-model="hideOk"
              type="checkbox"
              class="form-check-input"
              id="hideOkSwitch"
              true-value="1"
              false-value="0"
            />
            <label class="form-check-label h6 ms-2" for="hideOkSwitch">Hide OK</label>
          </div>
        </div>
        <div class="col-md-2 d-flex align-items-center">
          <div class="form-check form-switch form-switch-lg mb-0">
            <input
              v-model="showCharsets"
              type="checkbox"
              class="form-check-input"
              id="charsetSwitch"
              true-value="1"
              false-value="0"
            />
            <label class="form-check-label h6 ms-2" for="charsetSwitch">Include Charset</label>
          </div>
        </div>
        <div class="col-md-2 d-flex justify-content-end">
          <b-button variant="primary" @click="getDiffs(srcDb, dstDb, hideOk)">
            Apply Filters
          </b-button>
        </div>
      </div>
      <!-- Table -->
    <div class="row card-body">
      <div class="table-responsive mb-0 mt-4">
        <b-table
          :busy="isBusy"
          :items="diffs"
          :fields="[
            { key: 'table_name', label: 'Table' },
            { key: 'comment', label: 'Comment' },
            { key: 'sql_cmd', label: 'SQL Command' },
            { key: 'created_at', label: 'timestamp' }
          ]"
          responsive="sm"
          show-empty
          empty-text="No differences found"
        >
          <template v-slot:cell(comment)="row">
            <div class="flex-shrink-0">
              <span
                style="cursor:pointer; text-decoration:underline; color:blue;"
                @click="openDiffModal(row.item)"
              >
                {{ row.item.comment.split('\n')[0] }}...
              </span>
            </div>
          </template>
          <template v-slot:cell(sql_cmd)="row">
            <div>
              <pre v-if="expandedRows[row.item.table_name + '_sql']" style="white-space: pre-wrap;">
              {{ row.item.sql_cmd }}
              </pre>
                            <pre v-else style="white-space: pre-wrap; max-height: 75px; overflow: hidden;">
              {{ row.item.sql_cmd }}
              </pre>
              <b-button
                v-if="row.item.sql_cmd && row.item.sql_cmd.length >= 202"
                size="sm"
                variant="link"
                @click="toggleRowExpansion(row.item.table_name + '_sql')"
              >
                {{ expandedRows[row.item.table_name + '_sql'] ? 'Collapse' : 'Expand' }}
              </b-button>
            </div>
          </template>
        </b-table>
      </div>
    </div>
    </div>

    

    <!-- Diff Modal -->
    <b-modal
      id="diff-modal"
      v-model="showDiffModal"
      size="xl"
      title="Database Diff Details"
      hide-footer
      @hidden="closeDiffModal"
    >
      <div v-if="selectedDiff" class="d-flex">
        <div class="flex-fill me-2">
          <h6>Comment / Differences</h6>
          <pre style="white-space: pre; font-family: monospace; max-height: 500px; overflow:auto;">
{{ selectedDiff.comment }}
          </pre>
        </div>
      </div>
    </b-modal>
  </div>
</template>
